import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { TagsFormBody } from 'src/components/TagsFormBody';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { usePurchaseDataContext } from 'src/contexts/PurchaseDataContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { AddPurchasePaymentDialog } from 'src/dialogs/AddPurchasePaymentDialog';
import { ConvertCurrencyDialog } from 'src/dialogs/ConvertCurrency';
import { ConvertCurrencyForm } from 'src/dialogs/ConvertCurrency/ConvertCurrencyDialog';
import { EditPurchasePaymentDialog } from 'src/dialogs/EditPurchasePaymentDialog';
import { PurchaseCostDialog } from 'src/dialogs/PurchaseCostDialog';
import { useAdjustFxRate } from 'src/hooks/useAdjustFxRate';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useGetDefaultAutoGeneratedPaymentMethod } from 'src/hooks/useGetDefaultAutoGeneratedPaymentMethod';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  useUserCanLockPurchaseCommission,
  useUserCanUpdatePurchaseCommission,
  useUserCanUpdatePurchaseTags,
} from 'src/hooks/useUserHasPurchasePermissions';
import {
  BodySectionTitle,
  ListingNotesSection,
  PurchasePaymentStatusTextContainer,
  PurchaseVendorHeader,
  TicketDetailRowPaymentList,
  TicketsDetailRow,
} from 'src/modals/common';
import {
  ModalBodyDataContainer,
  ModalBodyHeaderContainer,
} from 'src/modals/Modal/Modal.styled';
import { IconsFill, PlusIcon } from 'src/svgs/Viagogo';
import { TicketGroupWithHandlers } from 'src/tables/PurchaseTicketTable/configs/TicketGroupTableColumnsConfig';
import { PurchaseTicketTable } from 'src/tables/PurchaseTicketTable/PurchaseTicketTable';
import { DEFAULT_AUTO_GENERATED_PAYMENT_METHOD } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { PURCHASE_PAYMENT_STATUS_TO_CID } from 'src/utils/constants/contentIdMaps';
import { newBigIntId, newIntId } from 'src/utils/idUtils';
import { posChangedField } from 'src/utils/posFieldUtils';
import { PurchaseOrderDetailsInput } from 'src/utils/purchaseUtils';
import { getCosts } from 'src/utils/ticketUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  AutoPoClient,
  CostDetail,
  Feature,
  PurchaseClient,
  PurchaseOrderCostType,
  PurchaseOrderDetails,
  PurchasePayment,
  PurchasePaymentMethod,
  PurchasePaymentMethodType,
  PurchasePaymentStatus,
  TicketGroupEditReason,
  UiMoney,
} from 'src/WebApiController';

import { CommissionSection } from './CommissionSection';
import { CostDetailsTable } from './CostDetailsTable';
import { DealSection } from './Deal/DealSection';
import { DeliverySection } from './DeliverySection/DeliverySection';
import { useGetListingAndSaleIds } from './hooks/useGetListingAndSaleIds';
import { PaymentDetailsTable } from './PaymentDetailsTable';
import { usePurchaseCurrencyConversion } from './PurchaseMainDetails.hooks';
import { recalculateUnitFullyLoadedCostPreview } from './PurchaseMainDetails.utils';
import { PurchaseMainDetailsConversionCurrencySelector } from './PurchaseMainDetailsConversionCurrencySelector';
import * as styles from './PurchaseModal.css';

type PurchaseMainDetailsProps = {
  highlightTicketGroupIds?: number[];
  disabled?: boolean;
  allowPaymentEdits: boolean;
  allowAdditionalCostEdits: boolean;
  onSecondaryAccountOpen: () => void;
};

export function PurchaseMainDetails({
  disabled,
  allowPaymentEdits,
  allowAdditionalCostEdits,
  highlightTicketGroupIds,
  onTicketGroupAction,
  onSecondaryAccountOpen,
}: PurchaseMainDetailsProps &
  Pick<TicketGroupWithHandlers, 'onTicketGroupAction'>) {
  const { posEntity: purchaseOrder } =
    useActivePosEntityContext<PurchaseOrderDetails>();
  const { setValue, getValues, watch } =
    useFormContext<PurchaseOrderDetailsInput>();

  const poId = watch('id');
  const { saleIds, listingIds } = useGetListingAndSaleIds({
    ticketGroups: purchaseOrder?.ticketGroups,
  });

  const { posEntity: purchase, setActivePosEntity } =
    useActivePosEntityContext<PurchaseOrderDetails>();

  const { loginContext } = useAppContext();

  const ticketGroups = watch('ticketGroups');
  const currencyCode = watch('currencyCode');

  const currency =
    currencyCode?.value ??
    loginContext?.user?.activeAccount?.currencyCode ??
    'USD';

  const payments = watch('payments');
  const additionalCosts = watch('additionalCosts');

  const [editMode, setEditMode] = useState(poId <= 0);

  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const { getUiCurrency } = useLocalizationContext();
  const { totalTicketCost, totalShipping, totalCost } = getCosts(
    ticketGroups,
    additionalCosts?.value
  );

  const totalPaymentAmount = (payments?.value ?? []).reduce((acc, payment) => {
    if (payment.paymentStatus == PurchasePaymentStatus.Paid) {
      acc += payment.paymentAmount.amt;
    }
    return acc;
  }, 0);

  const { trackError } = useClientLogContext();

  const inboundEmailId = useQuery({
    queryKey: [
      'AutoPoClient.getAutoPoClassifiedInboundEmailIdForEntity',
      activeAccountWebClientConfig,
      poId,
    ],
    queryFn: async () => {
      if (activeAccountWebClientConfig.activeAccountId == null) {
        return null;
      }
      return new AutoPoClient(
        activeAccountWebClientConfig
      ).getAutoPoClassifiedInboundEmailIdForEntity(poId, null);
    },
    enabled: activeAccountWebClientConfig.activeAccountId != null,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError(
          'AutoPoClient.getAutoPoClassifiedInboundEmailIdForEntity',
          error,
          {
            purchaseOrderId: poId,
          }
        );
      },
    },
  });
  const navigate = useNavigate();

  const insertFallbackPurchasePaymentMethod = useCallback(
    async () =>
      await tryInvokeApi(
        async () => {
          return await new PurchaseClient(
            activeAccountWebClientConfig
          ).insertPurchasePaymentMethod({
            id: newIntId(), // will let backend auto generate a new one
            display: DEFAULT_AUTO_GENERATED_PAYMENT_METHOD,
            name: DEFAULT_AUTO_GENERATED_PAYMENT_METHOD,
            type: PurchasePaymentMethodType.Other,
          } as PurchasePaymentMethod);
        },
        (error) => {
          showErrorDialog('PurchaseClient.insertPurchasePaymentMethod', error, {
            trackErrorData: {
              paymentMethodName: DEFAULT_AUTO_GENERATED_PAYMENT_METHOD,
            },
          });
        }
      ),
    [activeAccountWebClientConfig, showErrorDialog]
  );

  const { defaultAutoGeneratedPaymentMethod } =
    useGetDefaultAutoGeneratedPaymentMethod();

  const { refreshData } = usePurchaseDataContext();

  const {
    isVisible: isAdjustFxRateVisible,
    openDialog: openAdjustFxRateDialog,
    dialog: adjustFxRateDialog,
  } = useAdjustFxRate(purchaseOrder ?? null, () => {
    refreshData(true);
    // Refresh the active data so the PurchaseDetail dialog and table will have the new content
    setActivePosEntity(purchaseOrder?.id ?? 0, null, true);
  });

  const onMarkPurchaseAsPaid = useCallback(async () => {
    let paymentMethodId = null;

    const vendorAccount = getValues('vendorAccount');
    if (vendorAccount?.value?.dftPayMethodId != null) {
      // if vendor account has default payment, use it
      paymentMethodId = vendorAccount.value?.dftPayMethodId;
    } else if (defaultAutoGeneratedPaymentMethod?.id != null) {
      // if fallback default payment already exist for that seller, use it
      paymentMethodId = defaultAutoGeneratedPaymentMethod?.id;
    } else {
      // if neither vendor account payment or fallback payment exists, create the fallback payment for seller account
      paymentMethodId = await insertFallbackPurchasePaymentMethod();
    }

    const existingPayments = getValues('payments');
    const newPayments = [];
    const uiCurrency = getUiCurrency(currency);

    newPayments.push({
      paymentId: newBigIntId(),
      paymentStatus: PurchasePaymentStatus.Paid,
      paymentDueDate: purchase?.poDate,
      paymentDate: purchase?.poDate,
      paymentAmount: {
        amt: totalCost - totalPaymentAmount,
        disp: null,
        currency: uiCurrency.code,
        dec: uiCurrency.dec,
      } as UiMoney,
      currencyCode: uiCurrency.code,
      paymentMethod: {
        id: paymentMethodId, // backend only need the id for inserting payment
        display: '',
        name: '',
        type: PurchasePaymentMethodType.Other,
      },
      convertedPaymentAmount: null,
      convertedCurrencyCode: null,
      conversionRate: null,
      conversionDate: null,
      conversionUserId: null,
    } as PurchasePayment);

    if (existingPayments == null) {
      setValue('payments', posChangedField(newPayments));
    } else {
      setValue(
        'payments',
        posChangedField([...existingPayments!.value!, ...newPayments])
      );
    }
  }, [
    setValue,
    getValues,
    currency,
    defaultAutoGeneratedPaymentMethod,
    getUiCurrency,
    insertFallbackPurchasePaymentMethod,
    purchase,
    totalCost,
    totalPaymentAmount,
  ]);

  return (
    <>
      <ModalBodyHeaderContainer>
        <Stack direction="column" gap="s">
          <PurchaseMainDetailsConversionCurrencySelector />
          <PurchaseVendorHeader
            editMode={editMode}
            allowSearchAsVendorAccountInput={false}
            disabled={disabled}
            onToggleEditDetails={() => setEditMode(!editMode)}
            numOfSales={saleIds.size}
            numOfListings={listingIds.size}
            onGoToMessageThread={
              inboundEmailId.data
                ? () => {
                    navigate(`/messages/${inboundEmailId.data}`);
                  }
                : undefined
            }
            enableMarkAsPaid={totalCost > totalPaymentAmount}
            onMarkPurchaseAsPaid={onMarkPurchaseAsPaid}
            enableAdjustFxRate={isAdjustFxRateVisible}
            onAdjustFxRate={openAdjustFxRateDialog}
          />
        </Stack>
      </ModalBodyHeaderContainer>
      {adjustFxRateDialog}
      <ModalBodyDataContainer>
        <PurchaseMainDetailsBody
          disabled={disabled}
          allowPaymentEdits={allowPaymentEdits}
          allowAdditionalCostEdits={allowAdditionalCostEdits}
          numOfSales={saleIds.size}
          highlightTicketGroupIds={highlightTicketGroupIds}
          onTicketGroupAction={onTicketGroupAction}
          onSecondaryAccountOpen={onSecondaryAccountOpen}
        />
      </ModalBodyDataContainer>
    </>
  );
}

export function PurchaseMainDetailsBody({
  disabled,
  allowPaymentEdits,
  allowAdditionalCostEdits,
  highlightTicketGroupIds,
  onTicketGroupAction,
  onSecondaryAccountOpen,
}: PurchaseMainDetailsProps &
  Pick<TicketGroupWithHandlers, 'onTicketGroupAction'> & {
    numOfSales: number;
  }) {
  const { setValue, getValues, watch } =
    useFormContext<PurchaseOrderDetailsInput>();
  const { posEntity: purchase } =
    useActivePosEntityContext<PurchaseOrderDetails>();

  const hasAddCostOnCancelledPurchaseFeature = useUserHasFeature(
    Feature.AllowAddCostOnCancelledPO
  );
  const hasFullyLoadedUnitCostFeature = useUserHasFeature(
    Feature.FullyLoadedUnitCost_UseStoredValue
  );

  const canEditTags = useUserCanUpdatePurchaseTags(purchase);
  const canEditCommission = useUserCanUpdatePurchaseCommission(purchase);
  const canLockUnlockCommission = useUserCanLockPurchaseCommission(purchase);

  const { loginContext } = useAppContext();
  const currentUserId = loginContext?.user?.userId!;
  const costDialog = useBasicDialog();
  const addPaymentDialog = useBasicDialog();
  const editPaymentDialog = useBasicDialog();
  const convertCurrencyDialog = useBasicDialog();
  const [currentCostModel, setCurrentCostModel] = useState<CostDetail>();
  const [currentPayment, setCurrentPayment] = useState<PurchasePayment>();

  const { isConvertedPurchase, originalCurrency, targetCurrency } =
    usePurchaseCurrencyConversion();

  const ticketGroups = watch('ticketGroups');
  const currencyCode = watch('currencyCode');

  const currency =
    currencyCode?.value ??
    loginContext?.user?.activeAccount?.currencyCode ??
    'USD';

  const payments = watch('payments');
  const secondaryVendor = watch('secondaryVendor');
  const secondaryVendorAccount = watch('secondaryVendorAccount');
  const additionalCosts = watch('additionalCosts');
  const tags = watch('tags');
  const internalNotes = watch('privateNotes');

  const onSetPaymentPaid = useCallback(
    ({ paymentId }: PurchasePayment) => {
      const index = payments?.value?.findIndex(
        (p) => p.paymentId === paymentId
      );
      if (index != null && index >= 0) {
        const newPaymentDate = new Date().toISOString();
        if (newPaymentDate > payments!.value![index].paymentDueDate) {
          setValue(
            `payments.value.${index}.paymentStatus`,
            PurchasePaymentStatus.RefundNeeded
          );
        } else {
          setValue(
            `payments.value.${index}.paymentStatus`,
            PurchasePaymentStatus.Paid
          );
        }

        setValue(`payments.value.${index}.paymentDate`, newPaymentDate);
        setValue(`payments.hasChanged`, true);
      }
    },
    [payments, setValue]
  );
  const onEditPayment = useCallback(
    (payment: PurchasePayment) => {
      setCurrentPayment(payment);

      editPaymentDialog.launchDialog();
    },
    [editPaymentDialog]
  );
  const onEditPaymentSave = useCallback(
    (payment: PurchasePayment) => {
      const existingPayment = payments?.value?.find(
        (p) => p.paymentId === payment.paymentId
      );
      if (existingPayment) {
        payment.paymentId = existingPayment.paymentId;
        Object.assign(existingPayment, payment);

        setValue('payments', posChangedField(payments!.value));
      }

      editPaymentDialog.closeDialog();
    },
    [payments, editPaymentDialog, setValue]
  );
  const onEditPaymentCancel = useCallback(() => {
    setCurrentPayment(undefined);
    editPaymentDialog.closeDialog();
  }, [editPaymentDialog]);

  const onRemovePayment = useCallback(
    ({ paymentId }: PurchasePayment) => {
      const newPayments = payments?.value?.filter(
        (p) => p.paymentId !== paymentId
      );
      if (newPayments && newPayments?.length !== payments?.value?.length) {
        setValue('payments', posChangedField(newPayments));
      }

      editPaymentDialog.closeDialog();
    },
    [payments?.value, editPaymentDialog, setValue]
  );

  const onAddNewCost = useCallback(() => {
    setCurrentCostModel({
      id: newBigIntId(),
      cost: null,
      costType: PurchaseOrderCostType.Tax,
      date: new Date().toISOString(),
      description: null,
    } as CostDetail);

    costDialog.launchDialog();
  }, [costDialog, setCurrentCostModel]);

  const onAddNewCostSave = useCallback(
    (cost: CostDetail) => {
      const existingCost = additionalCosts?.value?.find(
        (p) => p.id === cost.id
      );
      if (existingCost) {
        existingCost.cost = cost.cost;
        existingCost.costType = cost.costType;
        existingCost.date = cost.date;
        existingCost.description = cost.description;

        setValue('additionalCosts', posChangedField(additionalCosts!.value));
      } else {
        setValue(
          'additionalCosts',
          posChangedField([...(additionalCosts?.value ?? []), cost])
        );
      }
      if (hasFullyLoadedUnitCostFeature) {
        recalculateUnitFullyLoadedCostPreview(setValue, getValues);
      }
      costDialog.closeDialog();
    },
    [
      additionalCosts,
      costDialog,
      hasFullyLoadedUnitCostFeature,
      setValue,
      getValues,
    ]
  );

  const onAddNewCostCancel = useCallback(() => {
    setCurrentCostModel(undefined);
    costDialog.closeDialog();
  }, [costDialog]);

  const onAddNewPayment = useCallback(() => {
    addPaymentDialog.launchDialog();
  }, [addPaymentDialog]);

  const onAddNewPaymentSave = useCallback(
    (payments: PurchasePayment[]) => {
      const existingPayments = getValues('payments');
      if (existingPayments == null) {
        setValue('payments', posChangedField(payments));
      } else {
        setValue(
          'payments',
          posChangedField([...existingPayments!.value!, ...payments])
        );
      }
      addPaymentDialog.closeDialog();
    },
    [setValue, getValues, addPaymentDialog]
  );

  const onAddNewPaymentCancel = useCallback(() => {
    addPaymentDialog.closeDialog();
  }, [addPaymentDialog]);

  const onEditCost = useCallback(
    (cost: CostDetail) => {
      setCurrentCostModel(cost);

      costDialog.launchDialog();
    },
    [costDialog]
  );

  const onRemoveCost = useCallback(
    (costId: number) => {
      const newCosts = additionalCosts?.value?.filter((p) => p.id !== costId);
      if (newCosts && newCosts?.length !== additionalCosts?.value?.length) {
        setValue('additionalCosts', posChangedField(newCosts));
        if (hasFullyLoadedUnitCostFeature) {
          recalculateUnitFullyLoadedCostPreview(setValue, getValues);
        }
      }
    },
    [additionalCosts, hasFullyLoadedUnitCostFeature, setValue, getValues]
  );

  const onInternalNotesChanged = useCallback(
    (notes?: string | null) => {
      return setValue('privateNotes', posChangedField(notes ?? null));
    },
    [setValue]
  );

  const onConvertCurrency = useCallback(
    (payment: PurchasePayment) => {
      setCurrentPayment(payment);
      convertCurrencyDialog.launchDialog();
    },
    [convertCurrencyDialog]
  );

  const onCancelConvertCurrency = useCallback(() => {
    convertCurrencyDialog.closeDialog();
    setCurrentPayment(undefined);
  }, [convertCurrencyDialog]);

  const onSaveConvertCurrency = useCallback(
    (formValues: ConvertCurrencyForm) => {
      if (currentPayment && payments) {
        const updatedPayment = Object.assign({}, currentPayment, formValues);
        const filteredPayments = (payments.value ?? []).map((payment) =>
          payment.paymentId === currentPayment.paymentId
            ? updatedPayment
            : payment
        );
        setValue('payments', posChangedField(filteredPayments));
      }
      convertCurrencyDialog.closeDialog();
    },
    [convertCurrencyDialog, currentPayment, payments, setValue]
  );

  return (
    <Stack direction="column" gap="m">
      <div className={styles.bodySection}>
        <DealSection />
        <Stack alignItems="center" justifyContent="spaceBetween">
          <span className={styles.headerTitle}>
            <Content id={ContentId.TicketsPurchased} />
          </span>
          {purchase?.pmtStat && (
            <PurchasePaymentStatusTextContainer className={purchase.pmtStat}>
              <Content id={PURCHASE_PAYMENT_STATUS_TO_CID[purchase.pmtStat]} />
            </PurchasePaymentStatusTextContainer>
          )}
        </Stack>
        <PurchaseTicketTable
          disabled={disabled}
          currencyCode={currencyCode?.value ?? purchase?.currency ?? 'USD'}
          ticketGroups={ticketGroups}
          highlightTicketGroupIds={highlightTicketGroupIds}
          onTicketGroupAction={onTicketGroupAction}
        />
        {!disabled && onTicketGroupAction && (
          <TicketsDetailRow>
            <Button
              variant={'link'}
              onClick={() =>
                onTicketGroupAction({
                  ticketGroupId: -1,
                  action: TicketGroupEditReason.AddTickets,
                })
              }
            >
              <PlusIcon size={vars.iconSize.s} fill={IconsFill.currentColor} />
              <Content
                id={
                  ticketGroups?.length
                    ? ContentId.AddTicketsForAnotherEvent
                    : ContentId.AddTickets
                }
              />
            </Button>
          </TicketsDetailRow>
        )}
      </div>
      {secondaryVendor && secondaryVendorAccount && (
        <DeliverySection
          disabled={disabled}
          vendor={secondaryVendor.value}
          vendorAccount={secondaryVendorAccount.value}
          onChangeClick={onSecondaryAccountOpen}
        />
      )}
      <div className={styles.bodySection}>
        <BodySectionTitle>
          <Content id={ContentId.CostAndCredits} />
        </BodySectionTitle>
        <TicketsDetailRow>
          <CostDetailsTable
            disabled={
              hasAddCostOnCancelledPurchaseFeature
                ? !allowAdditionalCostEdits
                : disabled
            }
            currencyCode={currency}
            ticketGroups={ticketGroups}
            costs={additionalCosts?.value}
            onEditCost={onEditCost}
            onAddNewCost={onAddNewCost}
            onRemoveCost={onRemoveCost}
          />
        </TicketsDetailRow>
      </div>
      <CommissionSection
        disabled={disabled}
        readonly={!canEditCommission}
        canLockUnlockCommission={canLockUnlockCommission}
      />
      <div className={styles.bodySection}>
        <BodySectionTitle>
          <Content id={ContentId.Payments} />
        </BodySectionTitle>
        <TicketDetailRowPaymentList>
          <PaymentDetailsTable
            disabled={!allowPaymentEdits}
            currencyCode={currency}
            payments={payments?.value}
            onSetAsPaid={onSetPaymentPaid}
            onEditPayment={onEditPayment}
            onRemovePayment={onRemovePayment}
            onConvertCurrency={onConvertCurrency}
          />
        </TicketDetailRowPaymentList>
        {allowPaymentEdits && (
          <TicketsDetailRow>
            <Button variant={'link'} onClick={onAddNewPayment}>
              <PlusIcon size={vars.iconSize.s} fill={IconsFill.currentColor} />
              <Content id={ContentId.AddLineItem} />
            </Button>
          </TicketsDetailRow>
        )}
      </div>
      <div className={styles.bodySection}>
        <BodySectionTitle>
          <Content id={ContentId.Notes} />
        </BodySectionTitle>
        <ListingNotesSection
          internalNotes={internalNotes?.value}
          disabled={disabled}
          onInternalNotesChanged={onInternalNotesChanged}
        />
      </div>
      <div className={styles.bodySection}>
        <BodySectionTitle>
          <Content id={ContentId.Tags} />
        </BodySectionTitle>

        <TagsFormBody
          tagsCurrentEntity={tags?.value ?? []}
          onTagsUpdate={(tags) => {
            setValue('tags', { value: tags, hasChanged: true });
          }}
          disabled={disabled}
          entityType={ActionOutboxEntityType.Purchase}
          canEditTags={canEditTags}
        />
      </div>

      {costDialog.dialogProps.isOpen && (
        <PurchaseCostDialog
          {...costDialog.dialogProps}
          cost={currentCostModel!}
          currencyCode={currency}
          onSave={onAddNewCostSave}
          onClosed={onAddNewCostCancel}
        />
      )}

      {addPaymentDialog.dialogProps.isOpen && (
        <AddPurchasePaymentDialog
          {...addPaymentDialog.dialogProps}
          defaultCurrencyCode={currency}
          originalCurrencyCode={
            isConvertedPurchase ? originalCurrency ?? undefined : undefined
          }
          convertedCurrencyCode={isConvertedPurchase ? targetCurrency : undefined}
          onSave={onAddNewPaymentSave}
          onCancel={onAddNewPaymentCancel}
        />
      )}
      {editPaymentDialog.dialogProps.isOpen && currentPayment && (
        <EditPurchasePaymentDialog
          {...editPaymentDialog.dialogProps}
          onSave={onEditPaymentSave}
          onCancel={onEditPaymentCancel}
          payment={currentPayment}
        />
      )}

      {convertCurrencyDialog.dialogProps.isOpen && currentPayment && (
        <ConvertCurrencyDialog
          {...convertCurrencyDialog.dialogProps}
          onSave={onSaveConvertCurrency}
          onCancel={onCancelConvertCurrency}
          payment={currentPayment}
          currentUserId={currentUserId}
        />
      )}
    </Stack>
  );
}
